import React, { useState } from 'react';
import { useI18n } from '../../i18n';
import { showShareToast } from './shareToast';

export function buildSharePayload({ url, title, text }) {
  const targetUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareText = text || title || '';
  return {
    url: targetUrl,
    title: title || 'Outfit Detective',
    text: shareText,
    fullText: [shareText, targetUrl].filter(Boolean).join(' '),
  };
}

function buildShareLinks({ url, title, text, media }) {
  const shareText = text || title || '';
  const fullText = [shareText, url].filter(Boolean).join(' ');
  const encodedUrl = encodeURIComponent(url || '');
  const encodedTitle = encodeURIComponent(title || '');
  const encodedText = encodeURIComponent(shareText);
  const encodedFullText = encodeURIComponent(fullText);
  const encodedMedia = encodeURIComponent(media || url || '');

  return [
    {
      id: 'email',
      icon: 'ph ph-envelope',
      href: `mailto:?subject=${encodedTitle}&body=${encodedFullText}`,
      external: false,
    },
    {
      id: 'whatsapp',
      icon: 'ph ph-whatsapp-logo',
      href: `https://api.whatsapp.com/send?text=${encodedFullText}`,
      external: true,
    },
    {
      id: 'pinterest',
      icon: 'ph ph-pinterest-logo',
      href: `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedMedia}&description=${encodedText}`,
      external: true,
    },
  ];
}

export default function SocialShareButtons({ url, title, text, media, label }) {
  const { t } = useI18n();
  const links = buildShareLinks({ url, title, text, media });
  const [copied, setCopied] = useState(false);
  const payload = buildSharePayload({ url, title, text });

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(payload.fullText);
      setCopied(true);
      showShareToast(t('share.copyToast'));
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  }

  async function nativeShare() {
    if (!navigator.share) return copyShareLink();
    try {
      await navigator.share({
        title: payload.title,
        text: payload.text,
        url: payload.url,
      });
    } catch {
      await copyShareLink();
    }
  }

  const whatsappLink = links.find((item) => item.id === 'whatsapp');
  const emailLink = links.find((item) => item.id === 'email');

  return (
    <div className="ct-social-share" aria-label={label || t('tryonShare.chooseChannel')}>
      <span>{label || t('tryonShare.chooseChannel')}</span>
      <div className="ct-beta-invite-actions ct-social-share-pills">
        {[whatsappLink, emailLink].filter(Boolean).map((link) => (
          <a
            key={link.id}
            className={`ct-share-pill ${link.id}`}
            href={link.href}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noopener noreferrer' : undefined}
            aria-label={t(`tryonShare.${link.id}`)}
            title={t(`tryonShare.${link.id}`)}
          >
            <i className={link.icon}></i>
            <span>{t(`share.channel.${link.id}`)}</span>
          </a>
        ))}
        <button
          type="button"
          className={`ct-share-pill copy${copied ? ' active' : ''}`}
          onClick={copyShareLink}
          aria-label={copied ? t('share.copyDone') : t('share.copy')}
          title={copied ? t('share.copyDone') : t('share.copy')}
        >
          <i className={copied ? 'ph ph-check' : 'ph ph-link-simple'}></i>
          <span>{copied ? t('share.copyDone') : t('share.copy')}</span>
        </button>
        <button type="button" className="ct-share-pill native" onClick={nativeShare}>
          <i className="ph ph-share-network"></i>
          <span>{t('share.channel.native')}</span>
        </button>
      </div>
    </div>
  );
}
